import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Box,
  Chip,
  Paper,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Icon
} from '@mui/material';
import { useApiCall } from '../utils/hooks';
import { pdfApi } from '../utils/api';

function ListPDFs() {
  const [result, setResult] = useState(null);
  const { loading, execute } = useApiCall();

  const handleListPDFs = async () => {
    try {
      const data = await execute(() => pdfApi.list());
      
      setResult({
        success: true,
        status: 'Success',
        data: data
      });
    } catch (error) {
      setResult({
        error: error.message,
        success: false
      });
    }
  };

  useEffect(() => {
    handleListPDFs();
  }, []);

  const renderPDFList = (pdfs) => {
    if (!pdfs || !Array.isArray(pdfs)) return null;

    return (
      <List>
        {pdfs.map((pdf, index) => (
          <ListItem key={index} divider>
            <ListItemIcon>
              <Icon>picture_as_pdf</Icon>
            </ListItemIcon>
            <ListItemText
              primary={pdf.name || `PDF ${index + 1}`}
              secondary={
                <Box>
                  {pdf.size && (
                    <Typography variant="caption" display="block">
                      Size: {pdf.size}
                    </Typography>
                  )}
                  {pdf.updated && (
                    <Typography variant="caption" display="block">
                      Updated: {new Date(pdf.updated).toLocaleString()}
                    </Typography>
                  )}
                  {pdf.url && (
                    <Typography variant="caption" display="block" sx={{ wordBreak: 'break-all' }}>
                      URL: {pdf.url}
                    </Typography>
                  )}
                </Box>
              }
            />
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            List PDFs Service
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            View all PDFs stored in the Google Cloud Storage bucket.
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            API Documentation
          </Typography>
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Chip label="GET" color="success" size="small" sx={{ mr: 1 }} />
            <Typography component="span" sx={{ fontFamily: 'monospace' }}>
              /api/list-pdfs
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            Returns a list of all PDFs in the GCS bucket with metadata.
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, flexGrow: 1 }}>
              PDF Files
            </Typography>
            <Button
              variant="outlined"
              onClick={handleListPDFs}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Icon>refresh</Icon>}
            >
              {loading ? 'Loading...' : 'Refresh'}
            </Button>
          </Box>

          {loading && !result && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {result && (
            <Box>
              {result.error ? (
                <Alert severity="error">
                  Error: {result.error}
                </Alert>
              ) : (
                <>
                  <Typography variant="body2" gutterBottom>
                    Status: <Chip 
                      label={result.status} 
                      color={result.success ? 'success' : 'error'} 
                      size="small" 
                    />
                  </Typography>
                  
                  {result.data?.files ? (
                    <>
                      <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
                        Found {result.data.files.length} PDF files
                      </Typography>
                      <Paper sx={{ mt: 1, maxHeight: 400, overflow: 'auto' }}>
                        {renderPDFList(result.data.files)}
                      </Paper>
                    </>
                  ) : (
                    <Paper sx={{ p: 2, bgcolor: 'grey.50', mt: 1 }}>
                      <pre style={{ 
                        margin: 0, 
                        fontFamily: 'monospace', 
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {JSON.stringify(result.data, null, 2)}
                      </pre>
                    </Paper>
                  )}
                </>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default ListPDFs;
